// [phase12cc] Public Trading UI — Institutional Catalyst Watch tile.
// Read-only advisory surface. Reads
// /api/v1/market-intelligence/catalysts/institutional-candidates via the
// same-origin nginx proxy (the same path the Adversyn Control dashboard
// uses). No order controls, no buy/sell language, no raw JSON.
import { useCallback, useEffect, useState } from "react";
import { Sparkles, Info, Compass, ChevronDown, ChevronRight, RefreshCw, AlertTriangle } from "lucide-react";
import { apiV1Base } from "../../lib/apiConfig";
import { SectionExplainer, TermHelp } from "../BeginnerHelp";

interface CatalystItem {
  symbol: string;
  company_name?: string | null;
  catalyst_type?: string | null;
  conviction_hint?: string | null;
  institutional_significance_score?: number | null;
  ai_stack_tags?: string[];
  reasons?: string[];
  advisory_only?: boolean;
}

interface ApiResponse {
  ok?: boolean;
  data?: {
    status?: string;
    items?: CatalystItem[];
    no_auto_trading?: boolean;
    operator_approval_required?: boolean;
    generated_at_utc?: string | null;
  };
}

// Phase 12CG-bis-SP/DR (2026-05-31) - same-page Lane Routing handoff +
// per-symbol Catalyst-to-Mover drilldown.

const LANE_ROUTING_SET_EVENT = "adversyn:set-lane-symbol";

function openInLaneRouting(symbol: string): void {
  if (!symbol) return;
  try {
    window.dispatchEvent(new CustomEvent(LANE_ROUTING_SET_EVENT, { detail: { symbol } }));
  } catch { /* no-op: older browsers without CustomEvent constructor */ }
  // Scroll the Lane Routing card into view if it is mounted on the same page.
  try {
    const el = document.querySelector('[data-testid="lane-routing-explainer"]');
    if (el && typeof (el as HTMLElement).scrollIntoView === "function") {
      (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
    }
  } catch { /* no-op */ }
}

interface CtmDetailItem {
  symbol?: string;
  catalyst_type?: string | null;
  headline?: string | null;
  institutional_score?: number | null;
  severity?: string | null;
  counterparty_quality_score?: number | null;
  ai_relevance_score?: number | null;
  ai_stack_layers?: string[];
  price_reaction_status?: string | null;
  price_change_since_catalyst_pct?: number | null;
  volume_confirmation_status?: string | null;
  volume_change_since_catalyst_pct?: number | null;
  technical_confirmation_status?: string | null;
  historical_reaction_status?: string | null;
  historical_reaction_window?: string | null;
  historical_price_change_pct?: number | null;
  signal_watch_status?: string | null;
  council_watch_status?: string | null;
  missed_catalyst_status?: string | null;
  why_it_matters?: string | null;
  what_to_watch_next?: string | null;
}

interface CtmDetailPayload {
  status?: string;
  symbol?: string;
  items?: CtmDetailItem[];
}

interface CtmEnvelope { ok?: boolean; data?: CtmDetailPayload | null; error?: { message?: string } | null }

function prettyStatus(v: string | null | undefined): string {
  if (!v) return "Unavailable";
  return v.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtPct(v: number | null | undefined): string {
  return typeof v === "number" ? `${v >= 0 ? "+" : ""}${v.toFixed(2)}%` : "—";
}

function CatalystDetail({ symbol }: { symbol: string }) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CtmDetailItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiV1Base()}/market-intelligence/catalyst-to-mover/${encodeURIComponent(symbol)}`, {
        credentials: "include", headers: { accept: "application/json" }, cache: "no-store",
      });
      if (!res.ok) { setError(`HTTP ${res.status}`); setLoading(false); return; }
      const body = (await res.json()) as CtmEnvelope;
      const items = body?.data?.items || [];
      setDetail(items[0] || null);
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [symbol]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-[12px] text-[var(--muted-text)] mt-2" data-testid={`catalyst-detail-loading-${symbol}`}>Loading Catalyst-to-Mover detail for {symbol}...</div>;
  }
  if (error) {
    return (
      <div className="mt-2 text-[12px] text-amber-300" data-testid={`catalyst-detail-error-${symbol}`}>
        <AlertTriangle className="inline w-3 h-3 mr-1.5" />
        Catalyst detail unavailable: {error}
      </div>
    );
  }
  if (!detail) {
    return <div className="text-[12px] text-[var(--muted-text)] mt-2" data-testid={`catalyst-detail-empty-${symbol}`}>No Catalyst-to-Mover detail returned for {symbol}.</div>;
  }
  const d = detail;
  return (
    <div className="mt-2 space-y-2 text-[12px]" data-testid={`catalyst-detail-${symbol}`}>
      {d.headline && (
        <div className="rounded border border-[var(--card-border)] bg-[var(--card-bg)] p-2 text-[var(--app-text)]">{d.headline}</div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Catalyst Type</span>{prettyStatus(d.catalyst_type)}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Severity</span>{prettyStatus(d.severity)}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Institutional Score</span>{d.institutional_score ?? "—"}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Counterparty Quality</span>{d.counterparty_quality_score ?? "—"}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">AI Relevance</span>{d.ai_relevance_score ?? "—"}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Signal Watch</span>{prettyStatus(d.signal_watch_status)}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Council Watch</span>{prettyStatus(d.council_watch_status)}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Missed Catalyst</span>{prettyStatus(d.missed_catalyst_status)}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Price Reaction</span>{prettyStatus(d.price_reaction_status)} {d.price_change_since_catalyst_pct != null ? `(${fmtPct(d.price_change_since_catalyst_pct)})` : ""}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Volume Confirmation</span>{prettyStatus(d.volume_confirmation_status)} {d.volume_change_since_catalyst_pct != null ? `(${fmtPct(d.volume_change_since_catalyst_pct)})` : ""}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Technical Confirmation</span>{prettyStatus(d.technical_confirmation_status)}</div>
        <div><span className="text-[var(--muted-text)] uppercase tracking-widest text-[9px] block">Historical Reaction</span>{prettyStatus(d.historical_reaction_status)} {d.historical_reaction_window ? `(${d.historical_reaction_window})` : ""} {d.historical_price_change_pct != null ? `(${fmtPct(d.historical_price_change_pct)})` : ""}</div>
      </div>
      {d.why_it_matters && <div className="text-[12px] text-[var(--app-text)]"><strong>Why it matters:</strong> {d.why_it_matters}</div>}
      {d.what_to_watch_next && <div className="text-[12px] text-[var(--app-text)]"><strong>What to watch next:</strong> {d.what_to_watch_next}</div>}
      <div className="text-[10px] text-[var(--muted-text)] italic">
        Advisory only. Honest UNAVAILABLE is shown when the catalyst date is missing, the snapshot is stale, or no provider source covers the symbol. This view never places trades.
      </div>
      <button
        type="button"
        onClick={() => load()}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
        data-testid={`catalyst-detail-refresh-${symbol}`}
      >
        <RefreshCw className="w-3 h-3" /> Refresh detail
      </button>
    </div>
  );
}


function prettyEventType(code?: string | null): string {
  if (!code) return "—";
  return code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettyConviction(code?: string | null): string {
  switch ((code || "").toUpperCase()) {
    case "HIGH_CONVICTION_WATCH":
      return "High conviction watch";
    case "PRIORITY_WATCH":
      return "Priority watch";
    case "WATCH":
      return "Watch";
    case "IGNORE":
      return "Ignore";
    default:
      return code || "—";
  }
}



function CatalystRow({ c }: { c: CatalystItem }) {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <li
      className="border border-[var(--card-border)] rounded-md px-3 py-2 text-[13px]"
      data-testid={`institutional-catalyst-watch-row-${c.symbol}`}
    >
      <div className="flex items-center flex-wrap gap-2">
        <span className="font-mono text-[var(--app-text)] font-bold">{c.symbol}</span>
        {c.company_name && (
          <span className="text-[var(--muted-text)] text-[12px] truncate max-w-[200px]">
            {c.company_name}
          </span>
        )}
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border bg-slate-500/15 text-slate-300 border-slate-500/30">
          {prettyEventType(c.catalyst_type)}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border bg-amber-500/15 text-amber-300 border-amber-500/30">
          {prettyConviction(c.conviction_hint)}
        </span>
        {typeof c.institutional_significance_score === "number" && (
          <span className="text-[11px] text-[var(--muted-text)]">
            score {c.institutional_significance_score}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => openInLaneRouting(c.symbol)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 text-[10px] uppercase tracking-widest hover:bg-emerald-500/15"
            data-testid={`icw-open-lane-routing-${c.symbol}`}
            title="Load this symbol in the Lane Routing card below"
          >
            <Compass className="w-3 h-3" /> Open in Lane Routing
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] text-[10px] uppercase tracking-widest"
            data-testid={`icw-toggle-detail-${c.symbol}`}
            aria-expanded={open}
          >
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {open ? "Hide detail" : "Detail"}
          </button>
        </span>
      </div>
      {c.ai_stack_tags && c.ai_stack_tags.length > 0 && (
        <div className="text-[11px] text-[var(--muted-text)] mt-1">
          AI Stack: {c.ai_stack_tags.join(", ")}
        </div>
      )}
      {open && <CatalystDetail symbol={c.symbol} />}
    </li>
  );
}

export default function InstitutionalCatalystWatch() {
  const [items, setItems] = useState<CatalystItem[]>([]);
  const [providerState, setProviderState] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          "/api/v1/market-intelligence/catalysts/institutional-candidates",
        );
        if (!r.ok) {
          if (!cancelled) {
            setErr(`HTTP ${r.status}`);
            setLoaded(true);
          }
          return;
        }
        const j: ApiResponse = await r.json();
        if (cancelled) return;
        const data = j?.data || {};
        setItems((data.items || []).slice(0, 5));
        setProviderState(data.status || null);
        setLoaded(true);
      } catch (e) {
        if (!cancelled) {
          setErr(String(e));
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      data-testid="institutional-catalyst-watch"
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4 space-y-3"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          <Sparkles className="h-4 w-4" />
          Institutional Catalyst Watch
        </div>
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
          Advisory only
        </span>
      </header>

      <p className="text-[12px] text-[var(--muted-text)] leading-snug flex items-start gap-2">
        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span>
          Forward-looking catalyst intelligence. Advisory only — not a trade
          recommendation.
        </span>
      </p>

      <SectionExplainer
        testId="beginner-help-institutional-catalyst-watch"
        title="Institutional Catalyst Watch beginner guide"
        what={<>This watches for important news or activity from major companies, governments, or large institutions that may explain why a symbol deserves research attention.</>}
        why={<>Large outside events can help explain why a stock is moving before the move becomes obvious in normal price tables.</>}
        lookAt={<>Look at the symbol, catalyst type, conviction wording, and the Detail panel for Catalyst-to-Mover context such as price reaction and volume confirmation.</>}
        notDo={<>It does not recommend orders, bypass risk controls, or change official Signal labels, the platform&apos;s original result tag for the signal.</>}
      >
        <TermHelp term="Institutional Catalyst" />
        <TermHelp term="Catalyst-to-Mover" />
        <TermHelp term="Price Reaction" />
        <TermHelp term="Volume Confirmation" />
      </SectionExplainer>

      {err && (
        <div
          className="text-[12px] text-amber-300"
          data-testid="institutional-catalyst-watch-error"
        >
          Catalyst data unavailable: {err}
        </div>
      )}

      {!err && loaded && items.length === 0 && (
        <p
          className="text-[12px] text-[var(--muted-text)]"
          data-testid="institutional-catalyst-watch-empty"
        >
          No institutional catalyst candidates today.
        </p>
      )}

      {items.length > 0 && (
        <ul className="space-y-2" data-testid="institutional-catalyst-watch-list">
          {items.map((c) => (
            <CatalystRow key={c.symbol} c={c} />
          ))}
        </ul>
      )}

      <div
        className="text-[10px] text-[var(--muted-text)] italic"
        data-testid="institutional-catalyst-watch-no-auto-notice"
      >
        Catalyst intelligence does not place orders, provide order instructions, or bypass risk controls.
        {providerState ? ` Provider: ${providerState}.` : ""}
      </div>
    </section>
  );
}
